/**
 * The book — `studio/clients.html`'s "All clients" table, one row per client.
 *
 * Nine columns and not one of them is worked out here: every figure is a field of
 * `StudioClientRowSchema`, which `readmodels/studio-clients-list.ts` answers.
 * `.docs/17-studio-read-models.md` puts the arithmetic there on purpose — allowance
 * burn is weighted by value, `% of value` is priced against the *current* rate card,
 * and a page that divided two of its own numbers would print a third answer.
 *
 * Four things the row decides, because they are renderings rather than facts:
 *
 *   - **A cancelled client is muted and is not a link.** The mockup's last row is
 *     Halcyon Coffee in `.muted` with no anchor; `ClientLink`'s `muted` prop is that
 *     case and its own comment says the page decides it.
 *   - **A client with no live retainer prints `—`, not `$0`.** `mrr`,
 *     `allowance_burn_pct` and `effective_rate` are each nullable and each mean "there
 *     is nothing to state" (`.docs/20` §Edge cases: "MRR `—`, burn `—`, state 'no
 *     retainer'"). Zero is a number somebody agreed to.
 *   - **A client that has never had a retainer is a live link, in the state "No
 *     retainer".** A project or one-off client is a client — not muted like a cancelled
 *     one — and its first cell says when the account was opened, with no block count,
 *     because it has no blocks to count. `stateOf` is the one place a null
 *     `retainer_status` becomes that state, for the column, the counts and the fold.
 *   - **The USD line under a native amount is printed only when they differ.**
 *     `mrr.usd_eq` equals `mrr.native` for a USD client, and `$970` under `$970`
 *     would read as two facts.
 *
 * The burn cell is a `Meter`, given `used` and `included` and never a width — the one
 * thing `Meter`'s own header asks a caller not to do.
 *
 * **Four of the nine cells are a value and a smaller line under it**, and all four are
 * `Lines` (`components/portal/Layout.tsx`) rather than a value followed by an `<Xs>`
 * span. Session 24 (R3-12): as spans they were two inline boxes with no whitespace
 * between them, and the table printed `€890$970` and `Cancelledat period end, 31 May`.
 * `Lines` owns the pairing, so there is no way to write the cell and get one line.
 */

import type { StudioClientRowView } from '@fareast/shared'

import { Lines } from '../../../components/portal/Layout.js'
import { Meter, WHOLE_IN_POINTS, percentagePoints } from '../../../components/portal/Meter.js'
import { Pill, Tag } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { B, DateText, Muted, Small, Xs } from '../../../components/portal/Text.js'
import { ClientLink } from '../../../components/studio/ClientLink.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { WHOLE_DIGITS, percentText } from '../../../format/percent.js'

/** What a cell prints when the read model has nothing to put in it. */
const NONE = '—'

/**
 * Money is printed without cents where there are none.
 *
 * Every MRR and overdue figure on `studio/clients.html` is whole — `$3,740`, `€890`,
 * `€1,780` — so `showCents: 'auto'` prints the mockup's own string. An effective rate
 * that is not whole keeps its cents (`$131.82`) rather than being rounded to the
 * mockup's `$132`: rounding an amount is arithmetic, and `money.ts` is the only place
 * in this repository where that happens.
 */
const MONEY = { showCents: 'auto' } as const

/** The retainer column's word, per status. */
const STATUS_WORD: Readonly<Record<string, string>> = {
  active: 'Active',
  past_due: 'Past due',
  paused: 'Paused',
  ending: 'Ending',
  cancelled: 'Cancelled',
  no_retainer: 'No retainer',
}

/**
 * The two statuses that get a chip, and which one.
 *
 * CONTRACT.md §2.6: `need` is "this needs your decision", `bad` is "genuinely
 * broken", and everything else has no chip at all because the column header already
 * frames it. Active is not a state; cancelled and ending are facts, not alarms.
 */
const STATUS_PILL: Readonly<Record<string, 'need' | 'bad'>> = {
  past_due: 'bad',
  paused: 'need',
}

/**
 * The six states the fold lists: `clients.html`'s five, in its order, and then a client
 * that has never had a retainer — the `no_retainer` seg's own word, so a narrowed tab
 * and its one state are spelt the same.
 */
export const STATUS_ORDER = [
  'active',
  'past_due',
  'paused',
  'ending',
  'cancelled',
  'no_retainer',
] as const

/** The word for one status. Exported so the fold and the column cannot disagree. */
export function statusWord(status: string): string {
  return STATUS_WORD[status] ?? status
}

/** A row's state: its retainer's status, or `no_retainer` where it has never had one. */
export function stateOf(row: StudioClientRowView): string {
  return row.retainer_status ?? 'no_retainer'
}

export type ClientsBookProps = {
  readonly rows: readonly StudioClientRowView[]
}

export function ClientsBook({ rows }: ClientsBookProps) {
  const format = useFormat()

  return (
    <Table>
      <THead>
        <Tr>
          <Th>Client</Th>
          <Th>Package</Th>
          <Th right>MRR</Th>
          <Th>Tier</Th>
          <Th>Retainer</Th>
          <Th>Allowance burn</Th>
          <Th right>$/h</Th>
          <Th right>Overdue</Th>
          <Th>Last contact</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => (
          <BookRow format={format} key={row.client} row={row} />
        ))}
      </TBody>
    </Table>
  )
}

/* --------------------------------------------------------------------- one row */

function BookRow({ row, format }: { row: StudioClientRowView; format: Formatter }) {
  const cancelled = row.retainer_status === 'cancelled'
  const neverRetained = row.retainer_status === null

  return (
    <Tr variant={row.under_scoped ? 'need' : undefined}>
      <Td>
        <ClientLink client={row.client} muted={cancelled} name={row.name}>
          {neverRetained ? (
            <>Since {format.day(row.since)}</>
          ) : (
            <>
              Since {format.day(row.since)} &middot;{' '}
              {format.qty(row.blocks, { one: 'block', other: 'blocks' })}
            </>
          )}
        </ClientLink>
      </Td>

      <Td>
        <Small>
          {row.package === null ? (
            <Muted>{NONE}</Muted>
          ) : cancelled ? (
            <Muted>{row.package}</Muted>
          ) : (
            row.package
          )}
        </Small>
      </Td>

      <Td right num>
        {row.mrr === null ? (
          <Muted>{NONE}</Muted>
        ) : (
          <Lines
            under={
              row.mrr.usd_eq.currency === row.mrr.native.currency ? undefined : (
                <Muted>{format.money(row.mrr.usd_eq, MONEY)}</Muted>
              )
            }
          >
            <B>{format.money(row.mrr.native, MONEY)}</B>
          </Lines>
        )}
      </Td>

      <Td>
        <Tag>{format.number(row.tier)}</Tag>
      </Td>

      <Td>
        <Small>
          <RetainerState note={row.retainer_note} status={stateOf(row)} />
        </Small>
      </Td>

      <Td>
        <BurnCell format={format} row={row} />
      </Td>

      <Td right num>
        {row.effective_rate === null ? (
          <Muted>{NONE}</Muted>
        ) : (
          format.money(row.effective_rate, MONEY)
        )}
      </Td>

      <Td right num>
        <Lines under={row.overdue_note === null ? undefined : <Muted>{row.overdue_note}</Muted>}>
          {row.overdue === null ? <Muted>{NONE}</Muted> : format.money(row.overdue, MONEY)}
        </Lines>
      </Td>

      <Td nowrap>
        <Muted>
          {row.last_contact === null ? NONE : <DateText style="day" value={row.last_contact} />}
        </Muted>
      </Td>
    </Tr>
  )
}

/* ------------------------------------------------------------------ the cells */

/** The state word, and the sub-line the read model wrote for it. */
function RetainerState({ status, note }: { status: string; note: string | null }) {
  const word = statusWord(status)
  const pill = STATUS_PILL[status]

  return (
    <Lines under={note === null ? undefined : <Muted>{note}</Muted>}>
      {pill === undefined ? <Muted>{word}</Muted> : <Pill state={pill}>{word}</Pill>}
    </Lines>
  )
}

/**
 * The burn meter, and the line under it.
 *
 * `allowance_burn_pct` is a **ratio** of what the package includes, weighted across
 * every block on the retainer — there is no `used`/`included` pair behind it to pass
 * through. So the meter's allowance is 100 and its use is that ratio read as a
 * percentage: past 100 the segment is ochre, which is the one thing the colour means
 * (§2.10 — "only that part costs money").
 *
 * The sub-line is the mockup's three, in its own order of precedence. A row that is
 * under-scoped says only that, because that is the finding; otherwise it is the
 * `% of value` and, where there is one, the extras approved and not yet billed.
 */
function BurnCell({ row, format }: { row: StudioClientRowView; format: Formatter }) {
  /*
   * No live retainer, no allowance to burn.
   *
   * `mrr` is null exactly when the retainer is no longer live, and a burn is a
   * fraction of what *this period's* package includes — a cancelled account has no
   * package this period, so `readmodels/studio-clients-list.ts`'s honest `0` would
   * read here as "used none of it" rather than "there is none". `.docs/20` §Edge
   * cases asks for `—` and this is the condition behind it.
   */
  if (row.allowance_burn_pct === null || row.mrr === null) {
    /* `block`, like every other `.xs` in this table: a cell's small line is a line. */
    return (
      <Xs block>
        <Muted>{NONE}</Muted>
      </Xs>
    )
  }

  const extras =
    row.extras_unbilled === null || row.extras_unbilled.amount === 0
      ? null
      : `${format.money(row.extras_unbilled, MONEY)} extras`
  const value =
    row.value_pct === null ? null : `${percentText(format, row.value_pct, WHOLE_DIGITS)} of value`
  const foot = row.under_scoped
    ? 'Under-scoped'
    : [value, extras].filter((one) => one !== null).join(' · ')

  return (
    <Lines under={foot === '' ? undefined : <Muted>{foot}</Muted>}>
      <Meter
        inline
        fractionDigits={WHOLE_DIGITS}
        included={WHOLE_IN_POINTS}
        label="Burn"
        used={percentagePoints(row.allowance_burn_pct)}
      />
    </Lines>
  )
}

/* ---------------------------------------------------------------- the counts */

export type BookCounts = {
  readonly underScoped: number
  /**
   * How many rows owe something past its due date — the rows with an Overdue figure.
   *
   * Not the `past_due` retainers: a client with no retainer can owe a one-off invoice,
   * and a head reading "0 past due" over a row printing €450 overdue is two facts that
   * cannot both be read. The money page the head links to ages every invoice either way.
   */
  readonly pastDue: number
  /** How many rows are in each retainer status. Zero states are absent. */
  readonly byStatus: ReadonlyMap<string, number>
}

/**
 * The subtotals the section head and the states fold print, over the rows on screen.
 *
 * DESIGN.md's *permitted derivation*: a subtotal of the table directly below it,
 * which reconciles by eye. Deliberately **not** a field on the read model — a count
 * over every client would contradict the table whenever the seg is narrowed, which is
 * the trap `concentration` avoids by being computed before the filter and printed
 * above it rather than beside it.
 */
export function bookCounts(rows: readonly StudioClientRowView[]): BookCounts {
  const byStatus = new Map<string, number>()
  for (const row of rows) {
    byStatus.set(stateOf(row), (byStatus.get(stateOf(row)) ?? 0) + 1)
  }
  return {
    underScoped: rows.filter((row) => row.under_scoped).length,
    pastDue: rows.filter((row) => row.overdue !== null).length,
    byStatus,
  }
}
